import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import SeeristAutomatedSectionContent from './SeeristAutomatedSectionContent';
import {
  DEFAULT_SEERIST_REQUEST,
  SeeristRequestBuilder,
} from './SeeristRequestBuilder';
import {
  useRefreshSitrepV2,
  useUpdateSitrepSectionV2,
} from './hooks/useSitrepsV2';

export function getSectionSeeristRequest(section) {
  const request =
    section?.automation?.seeristRequest ||
    section?.output_format_structured?.automation?.seeristRequest;
  return request || DEFAULT_SEERIST_REQUEST;
}

export function isAutomatedSeeristSection(section) {
  if (section?.type !== 'automated') return false;
  const provider =
    section.automation?.provider ||
    section.output_format_structured?.automation?.provider;
  // Other automated providers have their own panels; treat missing provider as legacy Seerist.
  if (
    provider === 'layerStats' ||
    provider === 'orgEvents' ||
    provider === 'incidents'
  ) {
    return false;
  }
  return provider === 'seerist' || !provider;
}

/**
 * Playground-style Seerist request + results for automated sitrep sections. No
 * free-text section body editor.
 *
 * @param {{
 *   sitrepId: string;
 *   section: object;
 *   selectedDatetime?: string;
 *   disabled?: boolean;
 * }} props
 */
export default function AutomatedSeeristSectionPanel({
  sitrepId,
  section,
  selectedDatetime = undefined,
  disabled = false,
}) {
  const [seeristRequestDraft, setSeeristRequestDraft] = useState(() =>
    getSectionSeeristRequest(section)
  );

  const updateSitrepSectionMutation = useUpdateSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();

  const version = section?.section_versions?.[0] || null;

  const isProcessing = useMemo(() => {
    if (!version) return false;
    const status = (version.status || '').toLowerCase();
    return (
      !!status &&
      status !== 'completed' &&
      status !== 'summarized' &&
      status !== 'error'
    );
  }, [version]);

  useEffect(() => {
    setSeeristRequestDraft(getSectionSeeristRequest(section));
  }, [
    section?.id,
    section?.automation,
    section?.output_format_structured?.automation,
  ]);

  const isBusy =
    disabled ||
    isProcessing ||
    updateSitrepSectionMutation.isPending ||
    refreshSitrepMutation.isPending;

  const handleSendRequest = () => {
    const automation = {
      provider: 'seerist',
      seeristRequest: seeristRequestDraft || DEFAULT_SEERIST_REQUEST,
    };
    const summary_options =
      section.output_format_structured?.summary_options || [];

    updateSitrepSectionMutation.mutate(
      {
        sitrepId,
        sectionId: section.id,
        updatedFields: {
          type: 'automated',
          automation,
          output_format_structured: {
            summary_options,
            automation,
          },
        },
      },
      {
        onSuccess: () => {
          refreshSitrepMutation.mutate(
            {
              id: sitrepId,
              selectedDatetime:
                selectedDatetime ||
                version?.timestamp ||
                new Date().toISOString(),
              sections: [section.id],
            },
            {
              onError: (error) => {
                toast.error('Failed to refresh section.');
                console.error('Error refreshing section:', error);
              },
            }
          );
        },
        onError: (error) => {
          toast.error('Failed to save Seerist request.');
          console.error('Error saving Seerist request:', error);
        },
      }
    );
  };

  return (
    <div className="AutomatedSeeristSectionPanel dark tw:text-foreground">
      <div className="SeeristAutomatedSectionConfig mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <div>
            <strong>Seerist request</strong>
            <small className="d-block text-muted">
              Configure query params and filters, then send the request to
              populate this section.
            </small>
          </div>
          <Button type="button" disabled={isBusy} onClick={handleSendRequest}>
            Send request
          </Button>
        </div>
        <SeeristRequestBuilder
          value={seeristRequestDraft}
          onChange={setSeeristRequestDraft}
        />
      </div>
      {isProcessing ? (
        <ReportsGeneratingPlaceholder />
      ) : version?.data_string ? (
        <SeeristAutomatedSectionContent dataString={version.data_string} />
      ) : (
        <p className="text-muted mb-0">
          Configure the request above and click Send request to fetch Seerist
          results.
        </p>
      )}
    </div>
  );
}
